import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Save, Moon, Sun, Clock, Server, Lock } from 'lucide-react';
import { API_BASE_URL } from '../../services/api';
import { SettingHelp } from './SettingHelp';
import { useTranslation } from '../../i18n/I18nContext';
import { applyTheme, type Theme } from '../../styles/theme';

const API_BASE = API_BASE_URL;

export function UnifiedSettings() {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [message, setMessage] = useState('');
  const { t, language, setLanguage } = useTranslation();
  
  const { data: settingsData, isLoading, refetch } = useQuery({
    queryKey: ['unified-settings'],
    queryFn: async () => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/settings`, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    }
  });
  
  useEffect(() => {
    if (settingsData?.settings) {
      setSettings(settingsData.settings);
      const nextLanguage = settingsData.settings.language;
      if (nextLanguage === 'de' || nextLanguage === 'en') {
        setLanguage(nextLanguage);
      }
      const nextTheme = settingsData.settings.theme as Theme | undefined;
      if (nextTheme === 'classic' || nextTheme === 'industrial') {
        applyTheme(nextTheme);
      }
    }
  }, [settingsData, setLanguage]);
  
  const saveMutation = useMutation({
    mutationFn: async (newSettings: Record<string, any>) => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/settings/batch`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });
      if (!res.ok) throw new Error('Failed to save');
      return res.json();
    },
    onSuccess: () => {
      setMessage(`✅ ${t('settings.saved')}!`);
      refetch();
      setTimeout(() => setMessage(''), 2000);
    },
    onError: () => setMessage(`❌ ${t('settings.saveFailed')}`)
  });
  
  const handleSave = () => saveMutation.mutate(settings);
  const updateSetting = (key: string, value: any) => setSettings(prev => ({ ...prev, [key]: value }));
  
  if (isLoading) return <div className="p-6 text-gray-400">{t('common.loading')}</div>;
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="shell-section-title text-lg font-semibold">{t('settings.general')}</h2>
        <button onClick={handleSave} disabled={saveMutation.isPending} className="shell-button flex items-center gap-2 px-4 py-2">
          <Save className="w-4 h-4" /> {saveMutation.isPending ? t('common.saving') : t('common.saveAll')}
        </button>
      </div>
      
      {message && <div className={`p-3 rounded ${message.startsWith('✅') ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}`}>{message}</div>}
      
      <div className="shell-section p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Refresh Interval */}
          <div>
            <label className="shell-section-title flex items-center gap-2 text-sm font-medium mb-2">
              <Clock className="w-4 h-4" /> {t('settings.refreshInterval')}
            </label>
            <div className="flex items-center gap-4">
              <input type="range" min="30" max="300" step="30" value={parseInt(settings.refresh_interval) || 30} onChange={(e) => updateSetting('refresh_interval', e.target.value)} className="flex-1" />
              <span className="shell-section-title min-w-[50px]">{settings.refresh_interval || 30}s</span>
            </div>
            <p className="shell-muted mt-1 text-xs">{t('settings.refreshIntervalHint')}</p>
            <SettingHelp settingKey="refresh_interval" />
          </div>
          
          {/* Theme */}
          <div>
            <label className="shell-section-title flex items-center gap-2 text-sm font-medium mb-2">
              {settings.theme === 'classic' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />} {t('common.theme')}
            </label>
            <select
              value={settings.theme || 'industrial'}
              onChange={(e) => {
                const nextTheme = e.target.value as Theme;
                updateSetting('theme', nextTheme);
                applyTheme(nextTheme);
              }}
              className="shell-input w-full px-3 py-2"
            >
              <option value="classic">☀️ {t('theme.classic')}</option>
              <option value="industrial">🏭 {t('theme.industrial')}</option>
            </select>
          </div>

          <div>
            <label className="shell-section-title flex items-center gap-2 text-sm font-medium mb-2">
              {t('common.language')}
            </label>
            <select
              value={settings.language || language}
              onChange={(e) => {
                const nextLanguage = e.target.value as 'de' | 'en';
                updateSetting('language', nextLanguage);
                setLanguage(nextLanguage);
              }}
              className="shell-input w-full px-3 py-2"
            >
              <option value="de">{t('language.german')}</option>
              <option value="en">{t('language.english')}</option>
            </select>
            <p className="shell-muted mt-1 text-xs">{t('settings.languageHint')}</p>
          </div>
          
        </div>
      </div>
      
      {/* Discovery Settings */}
      <div className="shell-section p-6">
        <h3 className="shell-section-title mb-4 flex items-center gap-2 text-lg font-semibold">
          <Server className="w-5 h-5 text-blue-500" /> {t('settings.discovery')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.hostIp')}</label>
            <input type="text" value={settings.discovery_host_ip || ''} onChange={(e) => updateSetting('discovery_host_ip', e.target.value)} placeholder="server-ip-or-dns" className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="discovery_host_ip" />
          </div>
          
          <div>
            <label className="shell-section-title mb-1 flex items-center gap-1 text-sm"><Lock className="w-3 h-3" /> {t('settings.apiUsername')}</label>
            <input type="text" value={settings.api_username || ''} onChange={(e) => updateSetting('api_username', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="api_username" />
          </div>
          
          <div>
            <label className="shell-section-title mb-1 flex items-center gap-1 text-sm"><Lock className="w-3 h-3" /> {t('settings.apiPassword')}</label>
            <input type="password" value={settings.api_password || ''} onChange={(e) => updateSetting('api_password', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="api_password" />
          </div>
        </div>
      </div>

      <div className="shell-section p-6">
        <h3 className="shell-section-title mb-4 text-lg font-semibold">{t('settings.infrastructure')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="shell-section-title mb-1 block text-sm">{t('settings.analyticsDbUrl')}</label>
            <input type="text" value={settings.analytics_db_url || ''} onChange={(e) => updateSetting('analytics_db_url', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="analytics_db_url" />
          </div>
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.financeDbHost')}</label>
            <input type="text" value={settings.finance_db_host || ''} onChange={(e) => updateSetting('finance_db_host', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="finance_db_host" />
          </div>
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.financeDbPort')}</label>
            <input type="text" value={settings.finance_db_port || ''} onChange={(e) => updateSetting('finance_db_port', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="finance_db_port" />
          </div>
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.financeDbUser')}</label>
            <input type="text" value={settings.finance_db_user || ''} onChange={(e) => updateSetting('finance_db_user', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="finance_db_user" />
          </div>
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.financeDbPassword')}</label>
            <input type="password" value={settings.finance_db_password || ''} onChange={(e) => updateSetting('finance_db_password', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="finance_db_password" />
          </div>
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.financeDbName')}</label>
            <input type="text" value={settings.finance_db_name || ''} onChange={(e) => updateSetting('finance_db_name', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="finance_db_name" />
          </div>
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.fredApiKey')}</label>
            <input type="text" value={settings.fred_api_key || ''} onChange={(e) => updateSetting('fred_api_key', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="fred_api_key" />
          </div>
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.newsApiKey')}</label>
            <input type="text" value={settings.newsapi_key || ''} onChange={(e) => updateSetting('newsapi_key', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="newsapi_key" />
          </div>
          <div className="md:col-span-2">
            <label className="shell-section-title mb-1 block text-sm">{t('settings.appRootDir')}</label>
            <input type="text" value={settings.app_root_dir || ''} onChange={(e) => updateSetting('app_root_dir', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="app_root_dir" />
          </div>
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.freqtradeRootDir')}</label>
            <input type="text" value={settings.freqtrade_root_dir || ''} onChange={(e) => updateSetting('freqtrade_root_dir', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="freqtrade_root_dir" />
          </div>
          <div>
            <label className="shell-section-title mb-1 block text-sm">{t('settings.freqtradeDataDir')}</label>
            <input type="text" value={settings.freqtrade_data_dir || ''} onChange={(e) => updateSetting('freqtrade_data_dir', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="freqtrade_data_dir" />
          </div>
          <div className="md:col-span-2">
            <label className="shell-section-title mb-1 block text-sm">{t('settings.agentRootDir')}</label>
            <input type="text" value={settings.freqtrade_agent_root_dir || ''} onChange={(e) => updateSetting('freqtrade_agent_root_dir', e.target.value)} className="shell-input w-full px-3 py-2" />
            <SettingHelp settingKey="freqtrade_agent_root_dir" />
          </div>
        </div>
      </div>
    </div>
  );
}
